import React, { useEffect, useState } from 'react';
import { Card, Table, Statistic, Row, Col, Progress, Button, message, Space, Tag } from 'antd';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import budgetApi from '../services/api/budget';

interface BudgetConfig {
  dailyLimitTokens: number;
  singleRoundLimit: number;
  pricePerKTokens: number;
}

interface BudgetStatus {
  date: string;
  usedTokens: number;
  dailyLimit: number;
  remainingTokens: number;
  usagePercent: number;
  usedCost: number;
  dailyBudget: number;
  isExhausted: boolean;
}

interface ApiUsageDaily {
  id: number;
  usageDate: string;
  totalOutputTokens: number;
  totalCost: number;
  requestCount: number;
  createdAt: string;
  updatedAt: string;
}

const BudgetConfig: React.FC = () => {
  const [config, setConfig] = useState<BudgetConfig | null>(null);
  const [status, setStatus] = useState<BudgetStatus | null>(null);
  const [history, setHistory] = useState<ApiUsageDaily[]>([]);
  const [loading, setLoading] = useState(false);

  // 加载预算配置
  const loadConfig = async () => {
    try {
      const res = await budgetApi.getConfig();
      setConfig(res);
    } catch (error) {
      console.error('加载配置失败', error);
    }
  };

  // 加载今日状态
  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await budgetApi.getStatus();
      setStatus(res);
    } catch (error) {
      console.error('加载状态失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 加载历史记录
  const loadHistory = async () => {
    try {
      const res = await budgetApi.getHistory(7);
      setHistory(res);
    } catch (error) {
      console.error('加载历史失败', error);
    }
  };

  // 重置今日预算
  const handleReset = async () => {
    try {
      const res = await budgetApi.resetTodayBudget();
      message.success('今日预算已重置');
      loadStatus();
    } catch (error) {
      message.error('重置失败');
    }
  };

  useEffect(() => {
    loadConfig();
    loadStatus();
    loadHistory();
  }, []);

  const historyColumns = [
    {
      title: '日期',
      dataIndex: 'usageDate',
      key: 'usageDate',
      render: (date: string) => date,
    },
    {
      title: '输出 Token',
      dataIndex: 'totalOutputTokens',
      key: 'totalOutputTokens',
      render: (tokens: number) => tokens.toLocaleString(),
    },
    {
      title: '费用 (元)',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (cost: number) => `¥${cost.toFixed(4)}`,
    },
    {
      title: '请求次数',
      dataIndex: 'requestCount',
      key: 'requestCount',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (time: string) => time?.replace('T', ' ').slice(0, 19),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>预算配置</h2>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadStatus(); loadHistory(); }}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleReset}
            danger
          >
            重置今日预算
          </Button>
        </Space>
      </div>

      {/* 配置信息 */}
      <Card title="配置参数" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="每日 Token 限额"
              value={config?.dailyLimitTokens || 0}
              suffix="tokens"
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="单轮最大 Token"
              value={config?.singleRoundLimit || 0}
              suffix="tokens"
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="单价"
              value={config?.pricePerKTokens || 0}
              suffix="元/千 tokens"
              precision={4}
            />
          </Col>
        </Row>
      </Card>

      {/* 今日使用状态 */}
      <Card title="今日使用情况" style={{ marginBottom: 16 }} loading={loading}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="已用 Token"
              value={status?.usedTokens || 0}
              valueStyle={{ color: status?.isExhausted ? '#ff4d4f' : '#3f8600' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="剩余 Token"
              value={status?.remainingTokens || 0}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已用金额"
              value={status?.usedCost || 0}
              prefix="¥"
              precision={4}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="每日预算"
              value={status?.dailyBudget || 0}
              prefix="¥"
              precision={4}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Progress
            percent={status?.usagePercent || 0}
            strokeColor={{
              '0%': '#108ee9',
              '100%': status?.isExhausted ? '#ff4d4f' : '#87d068',
            }}
            status={status?.isExhausted ? 'exception' : 'active'}
            format={(percent) => status?.isExhausted ? '已超额' : `${percent?.toFixed(1)}%`}
          />
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            {status?.isExhausted && (
              <Tag color="red">今日额度已用完</Tag>
            )}
          </div>
        </div>
      </Card>

      {/* 历史使用记录 */}
      <Card title="最近 7 天使用记录">
        <Table
          columns={historyColumns}
          dataSource={history}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: '暂无记录' }}
        />
      </Card>
    </div>
  );
};

export default BudgetConfig;
